import { Link } from "react-router-dom";

export default function ContentSection() {
  return (
    <>
      {/* What Is CSSBuy Spreadsheet */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
            What Is a CSSBuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
            A <strong>CSSBuy spreadsheet</strong> is a structured product catalog used by international buyers to discover, compare, and source products through the CSSBuy shopping agent. These carefully curated spreadsheets serve as a bridge between global shoppers and the vast marketplace of Chinese e-commerce platforms, making it effortless to find trending sneakers, streetwear essentials, luxury-inspired pieces, and everyday accessories without needing to navigate multiple foreign-language websites.
          </p>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
            Unlike browsing individual storefronts or scrolling through endless forum threads, a well-maintained spreadsheet gives you instant access to thousands of verified product links — complete with size references, pricing estimates, and community QC (quality check) photos. Whether you are building your first haul or restocking wardrobe staples, the right spreadsheet saves hours of manual searching and reduces the risk of ordering from unreliable sellers.
          </p>
          <Link
            to="/article/cssbuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold text-sm rounded-lg hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Learn More About Spreadsheets
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-2">
            Why Shoppers Trust Our Spreadsheet Recommendations
          </h2>
          <p className="text-sm md:text-base text-foreground-500 text-center mb-8 max-w-2xl mx-auto">
            We do not just throw together random product links. Every spreadsheet we feature has been tested, verified, and rated by the community.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg">
              <span className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-shield-check-line text-2xl text-accent-500"></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">Real-Time Updates</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Our featured spreadsheets are refreshed weekly. Dead links get removed, new trending products get added, and pricing columns stay current — so you are never working with outdated data.
              </p>
            </div>
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg">
              <span className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-group-line text-2xl text-accent-500"></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">Community Verified</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Thousands of buyers from Reddit, Discord, and agent forums have used these spreadsheets successfully. Their feedback directly shapes which lists we recommend and which ones we retire.
              </p>
            </div>
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg">
              <span className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-global-line text-2xl text-accent-500"></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">Multi-Agent Coverage</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                We cover more than just CSSBuy. Our comparison system spans Kakobuy, Superbuy, and Oopbuy — so you can pick the agent that offers the best shipping rates and service for your country.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Agents */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-2">
            Shopping Agents We Cover
          </h2>
          <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-xl mx-auto">
            We compare spreadsheet quality across the four most popular shopping agents in the community.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg hover:border-primary-300 transition-colors duration-200">
              <span className="block text-lg font-heading font-semibold text-primary-500 mb-1">CSSBuy</span>
              <span className="text-xs text-foreground-500">Most Spreadsheets</span>
            </div>
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg hover:border-foreground-300 transition-colors duration-200">
              <span className="block text-lg font-heading font-semibold text-foreground-800 mb-1">Kakobuy</span>
              <span className="text-xs text-foreground-500">Growing Fast</span>
            </div>
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg hover:border-foreground-300 transition-colors duration-200">
              <span className="block text-lg font-heading font-semibold text-foreground-800 mb-1">Superbuy</span>
              <span className="text-xs text-foreground-500">Established Player</span>
            </div>
            <div className="p-5 bg-background-50 border border-foreground-200 rounded-lg hover:border-foreground-300 transition-colors duration-200">
              <span className="block text-lg font-heading font-semibold text-foreground-800 mb-1">Oopbuy</span>
              <span className="text-xs text-foreground-500">New Contender</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20fashion%20lifestyle%20photography%20with%20warm%20beige%20and%20cream%20background%2C%20elegant%20curated%20product%20display%20on%20wooden%20surface%2C%20gentle%20natural%20window%20light%2C%20modern%20clean%20aesthetic%2C%20airy%20atmosphere%2C%20editorial%20quality%2C%20soft%20organic%20textures%2C%20no%20text%20no%20logos&width=1600&height=600&seq=css-cta-bg-02&orientation=landscape"
          alt="Shop the best CSSBuy spreadsheet collection"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/25 to-black/40"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-3">
            Ready to Start Building Your Next Haul?
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-6 max-w-lg mx-auto">
            Explore the full spreadsheet library on our main platform. Thousands of verified products, updated weekly, ready for your shopping agent.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-lg hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Explore Full Spreadsheet Library
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-foreground-900 font-semibold text-sm rounded-lg hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Visit Main Platform
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}